import { useFormik } from 'formik';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { TDocFile, TDocProject } from 'shared/constants/docs';
import { PROJECT_SCHEMA } from 'shared/constants/general';
import { emitUserError, emitUserInfo } from 'shared/constants/globals';
import { ROUTE } from 'shared/constants/routes_old';
import { PageContainer } from 'shared/Pages/common/PageContainer';
import { RecentContext } from 'shared/Providers/Recent';
import { UserContext } from 'shared/Providers/User';
import { TSelectedFile } from 'shared/utils/files/files';
import { prettyDate, prettySize } from 'shared/utils/format';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
	IconCloud,
	IconCloudWithCheck,
	IconLocalStorage,
	IconLocalStorageWithCheck,
	IconEdit,
} from 'shared/_ui/icon';
import Loading from 'shared/_components/loading';
import {
	Box,
	Button,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Switch,
	SxProps,
	Theme,
} from '@mui/material';
import { FormTextField } from 'shared/_ui/form/form-text-field';
import ProjectFiles from 'shared/_model/project-files';
import { useProjectContext } from 'shared/_pages/project/provider';
import useMenu from 'shared/hooks/use-menu';
import { FormTagField } from 'shared/_ui/form/form-tag-field';
import { getShareProperty, useSharingId } from 'shared/_model/sharing/utils';
import CopyToClipboardValue from 'shared/_components/copy-to-clipboard-value';
import ProjectSyncButton from 'app/_model/project/components/ProjectSyncButton';
import ShareDialog from 'shared/_model/sharing/ShareDialog';
import { SharingType } from 'shared/_model/sharing/types';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import {
	ExpandToggle,
	InfoCard,
	InfoCardSection,
	InfoGrid,
	InfoItemRoot,
	InfoItemText,
	LinkButton,
	MoreBlocks,
	MoreItems,
	NotesHead,
	StorageRow,
	StorageToggleRoot,
} from 'shared/_model/project-details/styles';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import moment from 'moment';

type TProjectDetailsProps = {
	// TODO: Remove unused props
	// projectId: string
	sharingId?: string;
	readProject: () => Promise<TDocProject>;
	saveProject?: (values: TDocProject) => Promise<void>;
	deleteProject?: () => Promise<string[]>;
	findFiles: () => Promise<TDocFile[]>;
	addFiles?: (files: TSelectedFile[]) => Promise<void>;
	removeFile?: (fileIds: string[]) => Promise<string[]>;
	saveFile?: (values: TDocFile) => Promise<void>;
};

/** Id of the project form; the Submit button sits in the page header, outside the form. */
const FORM_ID = 'project-details-form';

const fieldSx: SxProps<Theme> = {
	'.MuiFormLabel-root': {
		color: (theme) => theme.palette.secondary.main,
	},
};

/** Max width of the notes field (620px), same as the notes text in the new design. */
const NOTES_MAX_WIDTH = '38.75rem';

type InfoItemProps = {
	icon: ReactNode;
	/** What the value means; shown as a native tooltip and used as the accessible label. */
	title: string;
	mono?: boolean;
	/** Inline action after the value (e.g. a LinkButton). */
	action?: ReactNode;
	children: ReactNode;
};

/** Icon + value pair of the key-info grid. */
function InfoItem({ icon, title, mono, action, children }: InfoItemProps) {
	return (
		<InfoItemRoot title={title} aria-label={title} mono={mono}>
			{icon}
			<InfoItemText>{children}</InfoItemText>
			{action}
		</InfoItemRoot>
	);
}

type StorageToggleProps = {
	icon: ReactNode;
	label: string;
	checked: boolean;
	disabled?: boolean;
	onChange: (checked: boolean) => void;
};

/** Icon + label + switch row ("Cloud", "Local") at the top of the info card. */
function StorageToggle({ icon, label, checked, disabled, onChange }: StorageToggleProps) {
	return (
		<StorageToggleRoot>
			{icon}
			{label}
			<Switch
				checked={checked}
				disabled={disabled}
				onChange={(event) => onChange(event.target.checked)}
			/>
		</StorageToggleRoot>
	);
}

export default function ProjectDetails(props: TProjectDetailsProps): JSX.Element {
	const {
		permissions: { canUseCloud },
	} = useContext(UserContext);
	const history = useHistory();
	const { sharingId } = useSharingId();

	const { files, project, saveProject } = useProjectContext();

	const { addRecent } = useContext(RecentContext);
	const rm = useRouteMatch();

	const { deleteProject } = props;

	const [isEdit, setIsEdit] = useState(false);
	const handleEnableEdit = useCallback(() => {
		setIsEdit(true);
	}, []);

	const [showShareSettings, setShowShareSettings] = useState(false);
	const [showMore, setShowMore] = useState(false);
	// Editing needs the notes and keywords fields, so they are never hidden while editing.
	const isExpanded = showMore || isEdit;

	const projectSize = files.reduce((total, file) => total + (file.size || 0), 0);

	const handleSubmit = useCallback(
		async (values: Partial<TDocProject>) => {
			if (saveProject) {
				try {
					await saveProject(values);
					setIsEdit(false);
					emitUserInfo('Project updated!');
				} catch (e) {
					emitUserError((e as Error).message);
				}
			}
		},
		[saveProject]
	);

	const handleDelete = useCallback(async () => {
		if (deleteProject) {
			let answer = true;
			if (project.storeOnLocalDrive) {
				answer = window.confirm('Project will be removed.\nContinue?');
			}
			if (answer) {
				const errors = await deleteProject();
				if (!errors || errors.length === 0) {
					history.push(ROUTE.projects.root);
				} else {
					emitUserError(
						`Can not delete project. Reasons:\n\n${errors.reduce((acc, err) => `${acc}\n${err}`)}`
					);
				}
			}
		}
	}, [project.storeOnLocalDrive, deleteProject, history]);

	const handleShowShareSettings = useCallback(() => {
		setShowShareSettings((s) => !s);
	}, []);

	const formik = useFormik<Partial<TDocProject>>({
		initialValues: {},
		validationSchema: PROJECT_SCHEMA,
		onSubmit: handleSubmit,
	});

	useEffect(() => {
		if (project?._id && project.type) {
			addRecent({
				type: project.type,
				title: project.title,
				to: rm.url,
			});
		}
	}, [project?._id, project.type, project.title, rm.url, addRecent]);

	useEffect(() => {
		formik.setValues({
			description: project.description,
			tags: project.tags,
			title: project.title,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [project._rev]);

	const storageDisabled = !!sharingId || !saveProject || isEdit;
	const linkCount = getShareProperty(project.sharing).length;
	const canManageShare = !!saveProject && canUseCloud && !sharingId;

	const { anchorEl, open, handleOpen, handleClose } = useMenu();

	const actions = (
		<Box
			sx={{
				display: 'flex',
				gap: 2,
				alignItems: 'center',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					gap: 2,
					alignItems: 'center',
				}}
			>
				{isEdit && (
					<Button size="small" color="primary" type="submit" form={FORM_ID}>
						Submit
					</Button>
				)}
				{isEdit && (
					<Button
						size="small"
						color="secondary"
						onClick={() => {
							setIsEdit(false);
							formik.setValues(project);
						}}
					>
						Cancel
					</Button>
				)}
			</Box>
			{!!saveProject && !isEdit && <ProjectSyncButton projectId={project._id} />}
			{canManageShare && (
				<IconButton
					color="secondary"
					aria-label="Share"
					title="Share"
					disabled={isEdit}
					onClick={handleShowShareSettings}
				>
					<LinkOutlined />
				</IconButton>
			)}
			<IconButton color="secondary" aria-label="More" title="More" onClick={handleOpen}>
				<MoreHoriz />
			</IconButton>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				{!!saveProject && !isEdit && (
					<MenuItem
						onClick={() => {
							handleClose();
							handleEnableEdit();
						}}
					>
						<ListItemIcon>
							<IconEdit />
						</ListItemIcon>
						<ListItemText>Edit</ListItemText>
					</MenuItem>
				)}
				{!!deleteProject && isEdit && (
					<MenuItem
						disabled={!!sharingId}
						onClick={() => {
							handleClose();
							handleDelete();
						}}
						sx={{ color: 'error.main' }}
					>
						Delete
					</MenuItem>
				)}
			</Menu>
		</Box>
	);

	return (
		<PageContainer
			title={project.title}
			actions={project?._id ? actions : undefined}
			// TODO: remove next lines if they are not needed
			// parentTitle="Projects"
			// parentLink={!sharingId ? ROUTE.projects.root : undefined}
		>
			<Loading isLoading={!project?._id}>
				<form id={FORM_ID} onSubmit={formik.handleSubmit}>
					<InfoCard>
						<StorageRow>
							<StorageToggle
								icon={project.storeInCloud ? <IconCloudWithCheck /> : <IconCloud />}
								label="Cloud"
								checked={!!project.storeInCloud}
								disabled={storageDisabled}
								onChange={(storeInCloud) => saveProject?.({ storeInCloud })}
							/>
							<StorageToggle
								icon={project.storeOnLocalDrive ? <IconLocalStorageWithCheck /> : <IconLocalStorage />}
								label="Local"
								checked={!!project.storeOnLocalDrive}
								disabled={storageDisabled}
								onChange={(storeOnLocalDrive) => saveProject?.({ storeOnLocalDrive })}
							/>
						</StorageRow>
						{isEdit && (
							<FormTextField
								label="Title:"
								name="title"
								fullWidth
								formik={formik}
								sx={{ ...fieldSx, mb: 4 }}
								labelProps={{ width: '2.875rem' }}
								spellCheck
							/>
						)}
						<InfoGrid>
							<InfoItem icon={<CalendarTodayOutlined />} title="Created">
								{project.created ? moment(project.created).format('MMM D, YYYY') : ''}
							</InfoItem>
							<InfoItem icon={<IconLocalStorage />} title="Size">
								{prettySize(projectSize)}
							</InfoItem>
							<InfoItem icon={<BadgeOutlined />} title="Project ID" mono>
								<CopyToClipboardValue
									value={project?._id}
									sx={{
										'& .MuiTypography-root': {
											color: 'inherit',
											fontFamily: 'inherit',
											fontSize: 'inherit',
											fontWeight: 'inherit',
										},
									}}
								/>
							</InfoItem>
							<MoreItems expanded={isExpanded}>
								<InfoItem icon={<AccessTimeOutlined />} title="Last modified">
									{project.updated ? moment(project.updated).fromNow() : '—'}
								</InfoItem>
								<InfoItem
									icon={<LinkOutlined />}
									title="Shared links"
									action={
										canManageShare && (
											<LinkButton onClick={handleShowShareSettings}>Manage</LinkButton>
										)
									}
								>
									{linkCount === 0 ? 'No links' : `${linkCount} ${linkCount === 1 ? 'link' : 'links'}`}
								</InfoItem>
							</MoreItems>
						</InfoGrid>
						<ExpandToggle
							open={isExpanded}
							aria-expanded={isExpanded}
							onClick={() => setShowMore((value) => !value)}
						>
							{isExpanded ? 'Less info' : 'More info'}
							<ExpandMore />
						</ExpandToggle>
						<MoreBlocks expanded={isExpanded}>
							<InfoCardSection>
								<NotesHead>
									<DescriptionOutlined />
									Notes
								</NotesHead>
								<FormTextField
									name="description"
									fullWidth
									formik={formik}
									multiline
									placeholder="Add notes about your project"
									spellCheck
									readOnly={!isEdit}
									onReadOnlyClick={handleEnableEdit}
									sx={{ ...fieldSx, maxWidth: NOTES_MAX_WIDTH }}
								/>
							</InfoCardSection>
							<InfoCardSection sx={{ mt: 2, pt: 2 }}>
								<FormTagField
									name="tags"
									fullWidth
									formik={formik}
									readOnly={!isEdit}
									onReadOnlyClick={handleEnableEdit}
									sx={fieldSx}
									// showCopyButton // TODO: Implement copy tags functionality
								/>
							</InfoCardSection>
						</MoreBlocks>
					</InfoCard>
					<ProjectFiles />
				</form>
			</Loading>
			{showShareSettings && (
				<ShareDialog
					documentId={project._id}
					type={SharingType.PROJECT}
					handleClose={() => setShowShareSettings((s) => !s)}
				/>
			)}
		</PageContainer>
	);
}
